import JobTasksApiRequest from '@/api/job-tasks'
import WorkOrderApiRequest from '@/api/work-orders'
import { JobExpandablePropertyType, JobsFilterType, JobType } from '@/enum'
import {
  CustomFilterTypeExpression,
  DeleteTypeExpression,
  ItemTypeExpression,
  ListFilterTypeExpression,
  ListResponseTypeExpression
} from '@/enum/LinqExpressionType'
import NotificationType from '@/enum/NotificationType'
import StatusCodeType from '@/enum/StatusCodeType'
import { CalendarEvent, Job, JobScheduleOptions } from '@/models'
import WorkOrderJobForm from '@/models/jobs/work-orders/WorkOrderJobForm'
import ErrorHandlerService from '@/services/ErrorHandlerService'
import { JOB_DETAILS_EXPAND_PARAMS, MESSAGE_CONSTANTS, Network } from '@/utils/constants'
import CONSTANTS from '@/utils/constants/string-constants'
import {
  fetchUntilExhaustion,
  generateFilterExpressionForCalendarEvents,
  pluralize
} from '@/utils/helpers'
import { defineStore } from 'pinia'
import useGlobalStore from '../global'
import usePaginationStore from '../pagination'
import getters from './getters'
import state from './state'
import JobRequestMapper from '@/mappers/JobRequestMapper'
import type { IExplicitlyUpdatedJobProperty, IWorkOrderForm } from '@/interfaces'
import { expandedProperties } from '@/utils/constants'
import ErrorType from '@/enum/ErrorType'
import { JobsApiRequest } from '@/api'

const useWorkOrdersStore = defineStore('workOrdersStore', {
  state: () => state,
  getters,
  actions: {
    async getWorkOrders(
      storeName: string,
      responseOptions?: Record<string, string | Record<string, string | string[] | number>>
    ) {
      const workOrdersStore = usePaginationStore(storeName)
      const workOrdersApi = new WorkOrderApiRequest()
      const existingFilters = workOrdersStore.filtersOptions[ListFilterTypeExpression.Filter]
      responseOptions = {
        ...responseOptions,
        [ListResponseTypeExpression.Expand]: JOB_DETAILS_EXPAND_PARAMS,
        [ListFilterTypeExpression.Filter]: { ...existingFilters, Type: JobType.WORK_ORDER }
      }
      workOrdersStore.handleListAPICall<Job>(
        workOrdersApi.getWorkOrders.bind(workOrdersApi),
        Job,
        responseOptions
      )
    },
    async getOrdersForCalendar(start: string, end: string, options?: any) {
      const workOrdersApi = new WorkOrderApiRequest()
      const responseOptions = generateFilterExpressionForCalendarEvents(
        JobType.WORK_ORDER,
        start,
        end,
        options
      )
      const result = await fetchUntilExhaustion(
        workOrdersApi.getWorkOrders.bind(workOrdersApi),
        responseOptions
      )
      return result
        .map((job) => job)
        .filter((job) => job.scheduledAt || job.dueAt || job.startedAt || job.completedAt)
    },
    async getWorkOrdersUnshceduledJobs(storeName: string) {
      const unscheduleJobsListStore = usePaginationStore(storeName)
      const jobsApi = new JobsApiRequest()
      const expandableResources = [
        JobExpandablePropertyType.ASSIGNED_EMPLOYEES,
        JobExpandablePropertyType.ASSIGNED_EMPLOYEES_EMPLOYEE,
        JobExpandablePropertyType.SCHEDULE
      ]

      const responseOptions = {
        [ListResponseTypeExpression.Expand]: expandableResources.join(','),
        [ListFilterTypeExpression.Filter]: {
          [CustomFilterTypeExpression.Expression]: `(${JobsFilterType.SCHEDULED_DATE}==null && ${JobsFilterType.START_DATE}==null)`,
          [JobsFilterType.TYPE]: JobType.WORK_ORDER
        }
      }

      unscheduleJobsListStore.loading = true

      try {
        await unscheduleJobsListStore.handleListAPICall<CalendarEvent>(
          jobsApi.getJobs.bind(jobsApi),
          CalendarEvent,
          responseOptions
        )
        return true
      } catch (error) {
        return false
      } finally {
        unscheduleJobsListStore.loading = false
      }
    },
    async getWorkOrder(id: string) {
      this.isWorkOrderLoading = true
      const workOrdersApi = new WorkOrderApiRequest(false, true)

      try {
        const result = await workOrdersApi.getWorkOrder({
          id,
          [ItemTypeExpression.Expand]: JOB_DETAILS_EXPAND_PARAMS
        })
        if (result.status === StatusCodeType.Success && result.data) {
          this.workOrderForm = new WorkOrderJobForm(result.data)

          if (!this.workOrderForm.isRecurring()) {
            const jobTasksApi = new JobTasksApiRequest()
            const tasksResponse = await jobTasksApi.getJobTasks({ jobId: result.data.id })
            if (
              tasksResponse.status === StatusCodeType.Success &&
              tasksResponse.data?.value?.length
            ) {
              this.oneTimeWorkOrderTask = tasksResponse.data.value[0]
              this.workOrderForm.dueDate = this.oneTimeWorkOrderTask?.dueAt
            }
          }
        }
        return result
      } catch (error: any) {
        const title = error?.response.data?.detail
        const globalStore = useGlobalStore()
        globalStore.addError(
          {
            title
          },
          ErrorType.Network
        )
        return error
      } finally {
        this.isWorkOrderLoading = false
      }
    },

    async createWorkOrder() {
      const workOrdersApi = new WorkOrderApiRequest()
      const jobMapper = new JobRequestMapper()
      const globalStore = useGlobalStore()

      const newJob = jobMapper.toJobUpdate({ ...this.workOrderForm, type: JobType.WORK_ORDER })

      try {
        const response = await workOrdersApi.createWorkOrder(
          newJob?.jobRequestData,
          expandedProperties
        )

        if (response.status === StatusCodeType.Success && response.data) {
          globalStore.addToastNotification({
            id: Symbol('Job added'),
            message: `${response.data?.title ?? 'Work order'} added successfully`,
            type: NotificationType.Success
          })
        }

        return response
      } catch (error: any) {
        ErrorHandlerService.handleError(error, error)
        throw error
      }
    },

    async updateWorkOrder(
      id: string,
      explicitlyUpdatedWorkOrderProperty?: IExplicitlyUpdatedJobProperty
    ) {
      const workOrdersApi = new WorkOrderApiRequest(false, true)
      const jobMapper = new JobRequestMapper()
      const formData = (explicitlyUpdatedWorkOrderProperty as IWorkOrderForm) ?? this.workOrderForm
      const mappedJob = jobMapper.toJobUpdate({ ...formData, type: JobType.WORK_ORDER })
      try {
        const result = await workOrdersApi.updateWorkOrder(
          {
            id,
            ...expandedProperties
          },
          mappedJob.jobRequestData
        )

        if (result.status === StatusCodeType.Success) {
          const isOneTimeWorkOrder = !this.workOrderForm.isRecurring()
          const hasDueDateChanged = this.workOrderForm.dueDate !== this.oneTimeWorkOrderTask?.dueAt

          if (isOneTimeWorkOrder && hasDueDateChanged && this.oneTimeWorkOrderTask?.id) {
            const jobTasksApi = new JobTasksApiRequest()
            await jobTasksApi.updateJobTask(
              { jobId: id, taskId: this.oneTimeWorkOrderTask.id },
              {
                dueAt: this.workOrderForm.dueDate,
                status: this.oneTimeWorkOrderTask.status,
                updatedAt: this.oneTimeWorkOrderTask.updatedAt
              }
            )
          }
        }

        return result
      } catch (error: any) {
        const title =
          error?.response.data?.detail ?? MESSAGE_CONSTANTS.PURCHASE_ORDER_WAS_DELETED_ALERT_TITLE
        if ([StatusCodeType.NotFound, StatusCodeType.Invalid].includes(error.status)) {
          const globalStore = useGlobalStore()
          globalStore.addError(
            {
              title
            },
            ErrorType.Network
          )
          return error
        }
        ErrorHandlerService.handleError(error, error)
      }
    },

    resetRecurringJobSchedule() {
      if (this.workOrderForm.schedule) this.workOrderForm.schedule = new JobScheduleOptions()
    },

    async deleteWorkOrders(storeName: string) {
      const workOrdersApi = new WorkOrderApiRequest()
      const globalStore = useGlobalStore()
      const workOrdersListStore = usePaginationStore(storeName)

      try {
        let queryParams
        const workOrdersToDeleteIds = workOrdersListStore.selectedItems ?? []

        if (!workOrdersToDeleteIds.length) throw new Error('No purchase orders to delete')

        if (
          workOrdersListStore.someItemsSelected ||
          (workOrdersListStore.allItemsSelected && !workOrdersListStore.isSelectedAllSnackbarItems)
        )
          queryParams = { [DeleteTypeExpression.Expression]: { Id: workOrdersToDeleteIds } }

        const response = await workOrdersListStore.handleListDeleteAPICall(
          workOrdersApi.deleteWorkOrders.bind(workOrdersApi),
          queryParams
        )

        if (
          response?.status === StatusCodeType.Success &&
          response.data.succeeded > 0 &&
          response.data.failed === 0
        ) {
          globalStore.addToastNotification({
            id: Symbol(`Purchase orders deleted`),
            message: `${pluralize(response.data.succeeded, CONSTANTS.WORK_ORDERS)} ${CONSTANTS.DELETED_SUCCESSFULLY}`,
            type: NotificationType.Success
          })

          return response
        }

        if (response?.data && response.data.failed > 0) {
          globalStore.handleBulkOperationErrorsView(response, CONSTANTS.WORK_ORDERS)
          return Promise.resolve(response.data)
        }

        if (response?.data?.results.length) return Promise.reject(response.data)
        throw new TypeError(Network.TypeError.BulkOperationTypeMismatch)
      } catch (error: any) {
        ErrorHandlerService.handleAndCatchError(error, error)
      }

      return Promise.reject()
    },

    resetWorkOrderForm() {
      this.workOrderForm = new WorkOrderJobForm()
      this.oneTimeWorkOrderTask = null
    }
  }
})

export default useWorkOrdersStore
